import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { login, logout, readMe } from '../api/endpoints';
import { clearTokens, readAccessToken, writeTokens } from '../api/storage';
import type { AccountProfile } from '../api/types';

import { AuthContext, type AuthState } from './AuthContext';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [account, setAccount] = useState<AccountProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    const restore = async (): Promise<void> => {
      if (!readAccessToken()) {
        setIsLoading(false);

        return;
      }

      try {
        const profile = await readMe();

        if (!cancelled) {
          setAccount(profile);
        }
      } catch {
        clearTokens();
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void restore();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    const result = await login(email, password);

    writeTokens(result.tokens.accessToken, result.tokens.refreshToken);
    setAccount(result.account);
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await logout();
    } finally {
      clearTokens();
      setAccount(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ account, isLoading, signIn, signOut }),
    [account, isLoading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
